import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Layers, PenLine, Lightbulb, Library, User, Send, Linkedin } from "lucide-react";
import WorkTab from "./components/tabs/WorkTab";
import ThinkingTab from "./components/tabs/ThinkingTab";
import InnovationTab from "./components/tabs/InnovationTab";
import LibraryTab from "./components/tabs/LibraryTab";
import AboutTab from "./components/tabs/AboutTab";
import ConnectTab from "./components/tabs/ConnectTab";
import InboxPage from "./pages/InboxPage";
import ProjectPage from "./pages/ProjectPage";
import "./App.css";

const TABS = [
  { id: "work", label: "Work", Icon: Layers },
  { id: "thinking", label: "Thoughts", Icon: PenLine },
  { id: "innovation", label: "Innovation", Icon: Lightbulb },
  { id: "about", label: "About", Icon: User },
  { id: "library", label: "Library", Icon: Library },
  { id: "connect", label: "Connect", Icon: Send },
];

const CUES = {
  work: "See the argument below",
  thinking: "Read the thinking below",
  innovation: "Explore twelve years of innovation below",
  library: "Browse the talks, writing & audio below",
  about: "Meet the person behind the work below",
  connect: "Get in touch below",
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/project/:slug" element={<ProjectPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function Home() {
  const [tab, setTab] = useState("work");
  const [raised, setRaised] = useState(false);
  const heroRef = useRef(null);
  const navRef = useRef(null);
  const panelRef = useRef(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    const onScroll = () => setRaised(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => { lenis.destroy(); window.removeEventListener("scroll", onScroll); };
  }, []);

  const activateTab = (id) => {
    setTab(id);
    const heroH = heroRef.current?.offsetHeight || 0;
    const navH = navRef.current?.offsetHeight || 0;
    if (window.innerWidth < 768 || window.scrollY < heroH) {
      window.scrollTo({ top: heroH + navH - 10, behavior: "smooth" });
    }
  };

  const goInnovationTools = () => {
    setTab("innovation");
    const doScroll = (attempt = 0) => {
      const el = document.getElementById("innovation-tools");
      const navH = navRef.current?.offsetHeight || 0;
      if (!el) {
        if (attempt < 10) setTimeout(() => doScroll(attempt + 1), 60);
        return;
      }
      if (lenisRef.current) {
        lenisRef.current.scrollTo(el, { offset: -(navH + 10), duration: 1.1 });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - navH - 10;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };
    setTimeout(() => doScroll(), 120);
  };

  const onPanelMove = (e) => {
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--px", `${x * 14}px`);
    el.style.setProperty("--py", `${y * 10}px`);
  };

  return (
    <>
      <nav id="site-nav" ref={navRef} className={raised ? "raised" : ""}>
        <div className="nav-inner">
          <div className="nav-logo">Paulyn Duman</div>
        </div>
        <div className="tab-bar">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`tab-btn-${id}`}
              className={`tab-btn ${tab === id ? "active" : ""}`}
              onClick={() => activateTab(id)}
            >
              <Icon className="icon" />{label}
            </button>
          ))}
        </div>
      </nav>

      <section id="hero" ref={heroRef} data-testid="hero-section">
        <div className="hero-left">
          <div className="hero-eyebrow">Sustainable Development · Programmes &amp; Results · UN System</div>
          <h1 className="hero-name" data-testid="hero-name">
            <span className="reveal-line"><span className="reveal-inner">Paulyn</span></span>
            <span className="reveal-line"><span className="reveal-inner last">Duman<span className="dot">.</span></span></span>
          </h1>
          <p className="hero-lead" data-testid="hero-lead">Trained in law and public policy. Twenty years on one question: why well-resourced programmes still fail to reach people, and what it takes to close that gap.</p>
          <p className="hero-bio">Her conviction: sustainable development cannot succeed without learning that actually reaches people, shifts thinking, and enables action — and learning and knowledge are her instruments to achieve it. She works on why well-funded, well-designed programmes stall on the way to results, unpacking where they break and building the interventions that close the distance. From the halls of the Philippine Congress to UN platforms across the system, the work has been the same: turning a good decision on paper into a change in practice.</p>
          <p className="hero-cue" data-testid="hero-cue" onClick={() => {
            const heroH = heroRef.current?.offsetHeight || 0;
            const navH = navRef.current?.offsetHeight || 0;
            window.scrollTo({ top: heroH + navH - 10, behavior: "smooth" });
          }}>
            <span key={tab} className="hero-cue-text">↓ {CUES[tab]}</span>
          </p>
        </div>
        <div className="hero-right">
          <div className="hero-panel" ref={panelRef} onMouseMove={onPanelMove}>
            <div className="hc hc1"></div><div className="hc hc2"></div>
            <svg className="hero-globe" viewBox="0 0 360 420" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="180" cy="210" r="140" stroke="white" strokeWidth="1.2" />
              <ellipse cx="180" cy="210" rx="140" ry="48" stroke="white" strokeWidth="1" />
              <ellipse cx="180" cy="210" rx="140" ry="96" stroke="white" strokeWidth=".8" />
              <ellipse cx="180" cy="210" rx="50" ry="140" stroke="white" strokeWidth=".8" />
              <ellipse cx="180" cy="210" rx="100" ry="140" stroke="white" strokeWidth=".8" />
              <line x1="180" y1="70" x2="180" y2="350" stroke="white" strokeWidth="1" />
              <line x1="40" y1="210" x2="320" y2="210" stroke="white" strokeWidth="1" />
              <circle cx="180" cy="100" r="4" fill="white" /><circle cx="290" cy="160" r="3" fill="white" />
              <circle cx="280" cy="270" r="4" fill="white" /><circle cx="180" cy="320" r="3" fill="white" />
              <circle cx="80" cy="270" r="4" fill="white" /><circle cx="70" cy="160" r="3" fill="white" />
              <circle cx="230" cy="145" r="2.5" fill="white" /><circle cx="130" cy="280" r="2.5" fill="white" />
              <line x1="180" y1="100" x2="290" y2="160" stroke="white" strokeWidth=".8" />
              <line x1="290" y1="160" x2="280" y2="270" stroke="white" strokeWidth=".8" />
              <line x1="280" y1="270" x2="180" y2="320" stroke="white" strokeWidth=".8" />
              <line x1="180" y1="320" x2="80" y2="270" stroke="white" strokeWidth=".8" />
              <line x1="80" y1="270" x2="70" y2="160" stroke="white" strokeWidth=".8" />
              <line x1="70" y1="160" x2="180" y2="100" stroke="white" strokeWidth=".8" />
              <line x1="230" y1="145" x2="280" y2="270" stroke="white" strokeWidth=".6" />
              <line x1="130" y1="280" x2="70" y2="160" stroke="white" strokeWidth=".6" />
            </svg>
            <img src="/paulyn-duotone.png" alt="Duotone portrait of Paulyn Duman" className="hero-portrait" data-testid="hero-portrait" />
            <div className="hero-panel-label">
              <div className="hpl-sub">Currently at</div>
              <div className="hpl-org">the United Nations</div>
              <div className="hpl-loc">Knowledge Management and Learning Innovation</div>
            </div>
          </div>
        </div>
      </section>

      <div className="tab-panels">
        {tab === "work" && <WorkTab onGoInnovation={goInnovationTools} />}
        {tab === "thinking" && <ThinkingTab onGoLibrary={() => activateTab("library")} />}
        {tab === "innovation" && <InnovationTab />}
        {tab === "library" && <LibraryTab />}
        {tab === "about" && <AboutTab />}
        {tab === "connect" && <ConnectTab />}
      </div>

      <footer>
        <div className="fcopy">© 2026 Paulyn Duman · UNSSC Knowledge Centre for Sustainable Development · Bonn, Germany</div>
        <a className="flink" href="https://www.linkedin.com/in/paulynduman/" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link">
          <Linkedin className="icon" /> Connect on LinkedIn
        </a>
        <div className="ftag">UN 2.0 · SDGs · Knowledge Systems</div>
      </footer>
    </>
  );
}
